import { React, useEffect, useState, useRef } from 'react';
import './index2.css';
import '../App.css';
import './get-started.css';
import IntroVideo from '../images/intro_video.mp4';
import Crow_Logo from '../images/crow_electric3.png';
import VideoComponent from './VideoComponent.js';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useScrollBehavior } from './useScrollBehavior.js';
import SEO from '../SEO.js';
import Footer from './footer.js';

const LandingPage_No_Website = () => {
  
  useScrollBehavior();

  const heroRef = useRef(null);
  const sectionRef = useRef(null);
  const offerRef = useRef(null);

  const [isHeroVisible, setIsHeroVisible] = useState(false);
  const [isSectionVisible, setIsSectionVisible] = useState(false);
  const [isOfferVisible, setIsOfferVisible] = useState(false);

  const subtitleRef = useRef(null);
  const isSubtitleInView = useInView(subtitleRef, { once: true, amount: 0.5 });

  const title = "No Website? Get a Professional Business Website | Yokai Software";
const description =
  "No website? Yokai Software builds professional business websites designed to help you build trust, get found online, and turn visitors into customers.";
const canonical = "https://yokaisoftware.com/start-no-website";
const image = "https://yokaisoftware.com/yokai_apple_touch_icon2.png";

  const schema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": "https://yokaisoftware.com/#organization",
      "name": "Yokai Software",
      "url": "https://yokaisoftware.com/",
      "logo": "https://yokaisoftware.com/yokai_apple_touch_icon2.png",
      "description": "Yokai Software provides web design, web development, SEO, and digital solutions for businesses."
    },
    {
      "@type": "WebSite",
      "@id": "https://yokaisoftware.com/#website",
      "url": "https://yokaisoftware.com/",
      "name": "Yokai Software",
      "publisher": {
        "@id": "https://yokaisoftware.com/#organization"
      }
    },
    {
      "@type": "WebPage",
      "@id": "https://yokaisoftware.com/start-no-website#webpage",
      "url": "https://yokaisoftware.com/start-no-website",
      "name": "No Website? Get a Professional Business Website | Yokai Software",
      "isPartOf": {
        "@id": "https://yokaisoftware.com/#website"
      },
      "about": {
        "@id": "https://yokaisoftware.com/#organization"
      },
      "description": "No website? Yokai Software builds professional business websites designed to help you build trust, get found online, and turn visitors into customers."
    },
    {
      "@type": "Service",
      "@id": "https://yokaisoftware.com/start-no-website#service",
      "serviceType": "Business website design and website development",
      "provider": {
        "@id": "https://yokaisoftware.com/#organization"
      },
      "areaServed": "US",
      "url": "https://yokaisoftware.com/start-no-website",
      "description": "Professional business websites for companies that need an online presence, stronger credibility, and a website designed to help generate customers."
    }
  ]
};

  const letterAnimation = {
    hidden: { opacity: 0 },
    visible: (i) => ({
      opacity: 1,
      transition: {
        delay: i * 0.05,
        duration: 0.4
      }
    })
  };

  const renderAnimatedLettersByWord = (text, isInView) => {
    const words = text.split(' ');
    let k = 0;

    return words.map((word, wi) => (
      <span className="wordWrap" key={wi}>
        {word.split('').map((char, ci) => (
          <motion.span
            key={`${wi}-${ci}`}
            custom={k++}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            variants={letterAnimation}
            className="char"
          >
            {char}
          </motion.span>
        ))}
        {wi < words.length - 1 ? ' ' : null}
      </span>
    ));
  };

  const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: 'easeOut'
      }
    }
  };

  const handleScroll = () => {
    if (heroRef.current) {
      const rect = heroRef.current.getBoundingClientRect();
      setIsHeroVisible(rect.top < window.innerHeight - 80);
    }

    if (sectionRef.current) {
      const rect = sectionRef.current.getBoundingClientRect();
      setIsSectionVisible(rect.top < window.innerHeight - 120);
    }

    if (offerRef.current) {
      const rect = offerRef.current.getBoundingClientRect();
      setIsOfferVisible(rect.top < window.innerHeight - 120);
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div id="yokai_landing_wrapper">
  <SEO
    title={title}
    description={description}
    canonical={canonical}
    image={image}
    schema={schema}
  />

  <VideoComponent videoSrc={IntroVideo} />

  <div className="yokai_landing_module" id="yokai_landing_hero">
    <div id='yokai_landing_heading_container'>
      <div id='yokai_landing_heading_main' ref={subtitleRef}>
        {renderAnimatedLettersByWord("No Website", isSubtitleInView)}
      </div>

      <div id='yokai_landing_heading_accent'>
        {renderAnimatedLettersByWord("No Customers", isSubtitleInView)}
      </div>

      <div id='yokai_landing_heading_sub'>
        {renderAnimatedLettersByWord("If people can’t find you online, they won’t call you.", isSubtitleInView)}
      </div>

      <motion.div
        id="yokai_landing_intro_text"
        ref={heroRef}
        initial="hidden"
        animate={isHeroVisible ? "visible" : "hidden"}
        variants={fadeUp}
      >
        <p>
          If your business does not have a real website, potential customers may 
          question your credibility or never find you at all. Yokai Software builds 
          professional websites that help businesses establish trust, show up online, 
          and turn visitors into real leads.
        </p>

        <div id="yokai_landing_cta_row">
          <Link to="/ContactPage" className="yokai_landing_cta_primary">
            Get My Website
          </Link>
          <Link to="/ContactPage" className="yokai_landing_cta_secondary">
            Free Website Audit
          </Link>
        </div>

        <div id="yokai_landing_pill_row">
          <div className="yokai_landing_pill">Custom Design</div>
          <div className="yokai_landing_pill">SEO Focused</div>
          <div className="yokai_landing_pill">Mobile First</div>
        </div>
      </motion.div>
    </div>

    <div id='yokai_landing_logo_container'>
      <div id="yokai_landing_logo">
        <img src={Crow_Logo} alt="Yokai Software logo" className="yokai_landing_logo_img" />
      </div>
    </div>
  </div>

      <div className="module" id="landing_module_problem">
        <div id="landing_problem_container" ref={sectionRef}>
          <motion.div
            id="landing_problem_heading"
            initial="hidden"
            animate={isSectionVisible ? "visible" : "hidden"}
            variants={fadeUp}
          >
            <h1>Why a Business Without a Website Loses Opportunities</h1>
              <p>
                When a business has no real website, potential customers may not 
                find it, trust it, or understand its services clearly. A professional 
                website helps establish legitimacy, communicate value, and make it 
                easier for customers to take action.
              </p>
          </motion.div>

          <div id="landing_problem_cards">
            <motion.div
              className="landing_problem_card"
              initial="hidden"
              animate={isSectionVisible ? "visible" : "hidden"}
              variants={fadeUp}
              transition={{ delay: 0.15 }}
            >
              <h2>No Online Credibility</h2>
                <p>
                  Many customers expect a real website before they trust a business 
                  enough to call or submit an inquiry.
                </p>
            </motion.div>

            <motion.div
              className="landing_problem_card"
              initial="hidden"
              animate={isSectionVisible ? "visible" : "hidden"}
              variants={fadeUp}
              transition={{ delay: 0.3 }}
            >
              <h2>Harder to Get Found</h2>
                <p>
                  Without a proper website, it is much harder to appear in search 
                  results and give customers a place to learn about your services.
                </p>
            </motion.div>

            <motion.div
              className="landing_problem_card"
              initial="hidden"
              animate={isSectionVisible ? "visible" : "hidden"}
              variants={fadeUp}
              transition={{ delay: 0.45 }}
            >
              <h2>Lost Leads</h2>
                <p>
                  If people cannot quickly understand who you are, what you do, 
                  and how to contact you, they often move on to another business.
                </p>
            </motion.div>
          </div>
        </div>
      </div>

      <div className="module" id="landing_module_solution">
        <div id="landing_solution_container">
          <div id="landing_solution_left">
            <h1>Professional Websites Built to Establish Trust and Generate Leads</h1>

            <div className="landing_solution_item">
              <h2>A professional website for your business</h2>
                <p>
                  A real business website helps establish legitimacy and gives 
                  customers confidence in your company.
                </p>
            </div>

            <div className="landing_solution_item">
              <h2>Clear structure for visibility</h2>
                <p>
                  Better page hierarchy and service organization help search engines 
                  and users understand your business more clearly.
                </p>
            </div>

            <div className="landing_solution_item">
              <h2>Mobile-friendly design</h2>
                <p>
                  Many users will first see your business on a phone, so the site 
                  needs to look strong and function well there.
                </p>
            </div>

            <div className="landing_solution_item">
              <h2>Built to turn visitors into leads</h2>
                <p>
                  Clear messaging and strong calls to action help visitors understand 
                  your business and reach out.
                </p>
            </div>
          </div>

          <div id="landing_solution_right">
            <div className="landing_stat_box">
              <span>Built for</span>
              <h2>Lead Generation</h2>
            </div>
            <div className="landing_stat_box">
              <span>Designed for</span>
              <h2>Trust + Clarity</h2>
            </div>
            <div className="landing_stat_box">
              <span>Structured for</span>
              <h2>Search Visibility</h2>
            </div>
          </div>
        </div>
      </div>

      <div className="module" id="landing_module_offer">
        <motion.div
          id="landing_offer_box"
          ref={offerRef}
          initial="hidden"
          animate={isOfferVisible ? "visible" : "hidden"}
          variants={fadeUp}
        >
          <h1>Get Started Without Making It Complicated</h1>
          <p>
            If your business needs a real online presence, Yokai Software 
            can build a professional website that helps customers find you, 
            trust you, and contact you more easily.
          </p>

          <div id="landing_offer_options">
            <div className="landing_offer_option">
              <h2>One-Time Build</h2>
              <p>
                Ideal if you want a complete website project planned, built, and launched.
              </p>
            </div>

            <div className="landing_offer_option">
              <h2>Ongoing Support</h2>
              <p>
                Ideal if you want updates, improvements, optimization, and continued help after launch.
              </p>
            </div>
          </div>

          <div id="landing_offer_button_row">
            <Link to="/ContactPage" className="landing_cta_button_primary">
              Start Your Project
            </Link>
          </div>
        </motion.div>
      </div>

      <div className="module" id="landing_module_final">
        <div id="landing_final_container">
          <h1>Let's Build Something That Actually Works</h1>
          <p>
            A professional website helps your business look credible, show up online, 
            and give potential customers a clear next step.
          </p>
          <Link to="/ContactPage" className="landing_cta_button_primary">
            Contact Yokai Software
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default LandingPage_No_Website;